import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import Logo from "../components/Logo";
import {
  PlayArrow,
  Code,
  Article,
  BarChart,
  Factory,
  Security,
  Terminal,
  Storage,
  Analytics,
  Engineering,
  Handshake,
  Architecture,
  Support,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

export default function Home() {
  return (
    <Container>
      <Box
        className="inner-content"
        sx={{
          py: 5,
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
          }}
        >
          <Box sx={{ textAlign: { xs: "center", md: "left" }, flex: 1 }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Modern API for EUDR Compliance
            </Typography>

            <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
              Simplify EUDR integration for your development team with our powerful REST API. 
              Streamline data submission, automate compliance checks, and seamlessly incorporate EUDR into your IT workflows. 
              Our experienced development team can also provide custom integrations tailored to your specific needs.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Link to="/register">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  sx={{ px: 3, py: 1.5 }}
              >
                  Get Started
                </Button>
              </Link>
              
              <Link to="/docs">
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Code />}
                  sx={{ px: 3, py: 1.5 }}
                >
                  API Documentation
                </Button>
              </Link>

              <Link to="/contact">
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 3, py: 1.5 }}
                >
                  Contact Sales
                </Button>
              </Link>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flex: 1,
              mt: { xs: 4, md: 0 },
              maxWidth: { xs: "200px", md: "300px" },
              mx: "auto",
            }}
          >
            <Logo sx={{ width: "100%", height: "auto" }} />
          </Box>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Custom Tailored Solutions Section */}
        <Box 
          sx={{ 
            mb: 8, 
            bgcolor: "background.paper",
            borderRadius: 2,
            p: { xs: 4, md: 6 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
            }
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 3, 
              fontWeight: "bold", 
              textAlign: "center",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Custom Tailored EUDR Solutions
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 5, 
              textAlign: "center",
              color: "text.secondary",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Let us handle your entire EUDR compliance journey. From design to deployment, 
            we create bespoke solutions that seamlessly integrate with your existing systems.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              justifyContent: "center",
              mb: 5,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 18px)" },
                textAlign: "center",
              }}
            >
              <Engineering sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Full Development
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Our expert team designs and builds complete EUDR solutions tailored to your specific requirements
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 18px)" },
                textAlign: "center",
              }}
            >
              <Architecture sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                System Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seamless integration with your existing ERP, supply chain, and compliance systems
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 18px)" },
                textAlign: "center",
              }}
            >
              <Handshake sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Turn-key Solutions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Just define your needs - we handle everything from concept to deployment and maintenance
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 18px)" },
                textAlign: "center",
              }}
            >
              <Support sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Ongoing Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Continuous support and updates to ensure your EUDR compliance remains effortless
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 4,
              mb: 4,
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              How It Works
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 0 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>No API account needed</strong> - We handle all technical aspects
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Fixed project pricing</strong> - No API usage fees, just standard IT project pricing
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Complete ownership</strong> - The solution is built for you and belongs to you
              </Typography>
              <Typography component="li">
                <strong>Zero technical burden</strong> - Your only task is to define your requirements
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 3, fontStyle: "italic" }}>
              "Let our experienced development team transform your EUDR compliance challenges into a seamless, automated process"
            </Typography>
            <Link to="/contact">
              <Button
                variant="contained"
                size="large"
                sx={{ 
                  px: 4, 
                  py: 1.5,
                  fontSize: "1.1rem",
                }}
              >
                Discuss Your Custom Solution
              </Button>
            </Link>
          </Box>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Target Audience Section */}
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold" }}>
            Empowering Developers and IT Teams
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              justifyContent: "center",
            }}
          >
            <Box sx={{ width: { xs: "100%", md: "calc(33.33% - 32px)" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: "center" }}>
                  <Factory
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    For Producers & Suppliers
                  </Typography>
                  <Typography>
                    Seamlessly integrate EUDR compliance into your existing workflows. 
                    Our API provides tailored solutions for producers, compliance teams, developers, IT managers, and business users.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ width: { xs: "100%", md: "calc(33.33% - 32px)" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "background.paper",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: "center" }}>
                  <Security
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    For Compliance Teams
                  </Typography>
                  <Typography>
                    Automated due diligence workflows and documentation to
                    ensure products meet EUDR requirements
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ width: { xs: "100%", md: "calc(33.33% - 32px)" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: "center" }}>
                  <Terminal
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    For Developers
                  </Typography>
                  <Typography>
                    Modern REST API with comprehensive Swagger documentation and
                    robust security features
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ width: { xs: "100%", md: "calc(33.33% - 32px)" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: "center" }}>
                  <Storage
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    For IT Managers
                  </Typography>
                  <Typography>
                    Our API is designed with developers in mind, providing a seamless integration experience. 
                    IT managers can easily monitor usage, track metrics, and ensure reliable EUDR data submission.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ width: { xs: "100%", md: "calc(33.33% - 32px)" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: "center" }}>
                  <Analytics
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    For Business Users
                  </Typography>
                  <Typography>
                    Clear insights into your EUDR integration performance
                    without technical complexity
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Feature Grid */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}
          >
            Unlock the Power of REST API for EUDR Compliance
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 16px)",
                  lg: "calc(25% - 24px)",
                },
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  height: "100%",
                  borderTop: 3,
                  borderColor: "primary.main",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BarChart sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Modern REST API
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Our modern REST API allows developers to effortlessly submit, manage, and track EUDR due diligence statements. 
                  Say goodbye to complex SOAP integrations and hello to streamlined compliance.
                </Typography>
              </Paper>
            </Box>

            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 16px)",
                  lg: "calc(25% - 24px)",
                },
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  height: "100%",
                  borderTop: 3,
                  borderColor: "primary.main",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Article sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    API Security
                  </Typography>
                </Box>
                <Typography variant="body2">
                  We prioritize API security to safeguard your compliance data. 
                  With robust authentication and access control measures, your information remains secure and confidential.
                </Typography>
              </Paper>
            </Box>

            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 16px)",
                  lg: "calc(25% - 24px)",
                },
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  height: "100%",
                  borderTop: 3,
                  borderColor: "primary.main",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Code sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Custom Integrations
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Our skilled development team can work with you to build custom integrations and tailored software solutions based on our REST API, ensuring a perfect fit for your unique EUDR compliance needs.
                </Typography>
              </Paper>
            </Box>

            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 16px)",
                  lg: "calc(25% - 24px)",
                },
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  height: "100%",
                  borderTop: 3,
                  borderColor: "primary.main",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PlayArrow sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Developer Tools
                  </Typography>
                </Box>
                <Typography variant="body2">
                  We provide developer-friendly tools and resources to accelerate your EUDR API integration. 
                  Explore detailed documentation, code samples, and helpful guides to get started quickly.
                </Typography>
              </Paper>
            </Box>

            {/* Swagger UI Feature Card - Full Width */}
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 4, 
                    borderTop: 3,
                    borderColor: "secondary.main",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Code sx={{ fontSize: 32, color: "secondary.main", mr: 2 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Interactive Swagger UI
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Experience our comprehensive EUDR API through our interactive Swagger UI documentation. This powerful developer tool provides:
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Live API Testing</strong> - Execute real API calls directly from your browser
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Complete Endpoint Documentation</strong> - Detailed descriptions of all available endpoints, parameters, and response formats
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Request/Response Examples</strong> - See exactly what data to send and what you'll receive back
                        </Typography>
                        <Typography component="li" variant="body2">
                          <strong>Authentication Testing</strong> - Test your API keys and understand our security model
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                      <a href="/api-docs" target="_blank" rel="noopener noreferrer">
                        <Button variant="contained" color="secondary" size="large" sx={{ px: 4 }}>
                          Open Swagger UI
                        </Button>
                      </a>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 6,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
            Revolutionize Your EUDR Compliance with Our Developer-Centric API
          </Typography>
          <Typography sx={{ mb: 4 }}>
            Join forward-thinking companies leveraging our REST API to simplify their EUDR compliance journey. 
            Start your free trial today and experience the power of streamlined compliance for your development team.
          </Typography>
          <Link to="/register">
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Start Your Free Trial
            </Button>
          </Link>
        </Box>
      </Box>
    </Container>
  );
}
